import { useState } from 'react';
import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { _isNotBlank } from '@/littledash';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import Button from '@/components/UI/Button';
import DatePickerNative from '@/components/UI/DatePickerNative';
import Lookup from '@/components/UI/Lookup/Lookup';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { DateUtils } from '@/utils/Date.utils';

interface CreatedTask {
  id: string;
  date: string;
  status: string;
  description: string;
}

interface LegacyAddTaskProps {
  addNewTaskCallback: (task: CreatedTask) => void;
  closeModal: () => void;
}

interface FormValues {
  studyId: string;
  description: string;
  date: string;
  status: 'incomplete';
}

interface LookupSelection {
  value: string;
  label: string;
}

interface LoadOptionsArgs {
  inputValue: string;
  page: number;
}

interface ActiveStudy {
  id: string;
  name: string;
}

interface ActiveStudiesResponse {
  data: Array<ActiveStudy>;
  meta: { pagination: { current_page: number; total_pages: number } };
}

/** @deprecated retained for tenants on the `hide_tasks` legacy Tasks UI. */
const LegacyAddTask: FC<LegacyAddTaskProps> = ({ addNewTaskCallback, closeModal }) => {
  const [fetchError, setFetchError] = useState<unknown>(undefined);
  const [submissionError, setSubmissionError] = useState<unknown>(undefined);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      studyId: '',
      description: '',
      date: DateUtils.dateNow(),
      status: 'incomplete',
    },
  });

  const submit = async ({ date, studyId, description }: FormValues): Promise<void> => {
    try {
      const {
        data: { data: createdTask },
      } = await Http.post<{ data: CreatedTask }>(apiRoute('tasks.create', { study: studyId }), {
        date,
        studyId,
        description,
      });
      addNewTaskCallback(createdTask);
      closeModal();
    } catch (error) {
      setSubmissionError(error);
    }
  };

  const loadOptions = async ({ inputValue, page }: LoadOptionsArgs) => {
    const params = new URLSearchParams({ page: String(page) });
    if (_isNotBlank(inputValue)) {
      params.append('query', inputValue);
    }
    const url = `${apiRoute('studies.active')}?${params.toString()}`;
    try {
      const {
        data: {
          data,
          meta: { pagination },
        },
      } = await Http.get<ActiveStudiesResponse>(url);
      const { current_page: currentPage, total_pages: lastPage } = pagination;
      return {
        options: data.map((item) => ({ label: item.name, value: item.id })),
        hasMore: currentPage < lastPage,
      };
    } catch (error) {
      setFetchError(error);
      return { options: [], hasMore: false };
    }
  };

  return (
    <form
      className="ui__narrow-modal pa3"
      onSubmit={handleSubmit(submit)}
      data-test-component="LegacyAddTask"
      data-test-element="form"
    >
      <div className="center pb3">
        <h3 className="normal f4 pb2 tc">Add a new task</h3>
        {Boolean(submissionError) && (
          <ApiErrorBanner className="mb4" errorType={ApiErrorType.SUBMIT} error={submissionError} />
        )}
        {Boolean(fetchError) && <ApiErrorBanner className="mb4" errorType={ApiErrorType.FETCH} error={fetchError} />}
      </div>
      <div>
        <div className="pb4">
          <label htmlFor="legacy-add-task-study">Associated active study</label>
          <Controller
            name="studyId"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Lookup
                inputId="legacy-add-task-study"
                loadOptions={loadOptions}
                placeholder="Choose a study"
                handleSelect={(data: LookupSelection | null) => field.onChange(data?.value ?? '')}
              />
            )}
          />
          {Boolean(errors.studyId) && <p className="f6 red db pt2">Required</p>}
        </div>
        <div className="pb2">
          <label htmlFor="legacy-add-task-description">Task description</label>
          <input
            id="legacy-add-task-description"
            data-testid="description"
            type="text"
            placeholder="E.g Dose animals"
            {...register('description', {
              required: 'Required',
              minLength: { value: 3, message: 'Please enter more than 3 characters' },
              maxLength: { value: 50, message: 'Please enter less than 50 characters' },
            })}
          />
          <ErrorMessage
            errors={errors}
            name="description"
            render={({ message }) => <p className="f6 red db pb2">{message}</p>}
          />
        </div>
        <div className="pb3">
          <label htmlFor="legacy-add-task-date">Date</label>
          <Controller
            name="date"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <DatePickerNative
                id="legacy-add-task-date"
                name="date"
                value={field.value}
                onChange={field.onChange}
                style={{ marginBottom: 0, width: '100%' }}
              />
            )}
          />
        </div>
      </div>
      <div className="pt3 bt b--moon-gray">
        <Button submit className="w-100 mb2" disabled={isSubmitting} loading={isSubmitting} testId="submit">
          Create new task
        </Button>
        <Button className="w-100" plain onClick={closeModal} testId="cancel">
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default LegacyAddTask;
